import { Dialog, Transition } from "@headlessui/react";
import React, { Fragment, useEffect, useState } from "react";
import {
  FolderPlusIcon,
  FolderOpenIcon,
  PlusCircleIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import classNames from "classnames";
import FullWidthButton from "../FullWidthButton/FullWidthButton";
import FullWidthAsyncButton from "../FullWidthButton/FullWidthAsyncButton";
import SuggestionsInput from "../SuggestionsInput/SuggestionsInput";
import axiosClient from "../../axiosClient";
import { useNavigate } from "react-router-dom";

type ActionType = "add-to-existing" | "create-new-program";

type CurrentStep = "select-action-step" | "configure-step" | "success-step";

interface SuccessResult {
  programId: string;
  workoutTemplateId: string;
  programTitle: string;
}

interface AddWorkoutToProgramModalProps {
  isOpen: boolean;
  onModalClose: () => void;
  workout: Workout | null;
  programs: Program[];
}

const AddWorkoutToProgramModal: React.FC<AddWorkoutToProgramModalProps> = ({
  isOpen,
  onModalClose,
  workout,
  programs,
}) => {
  const [actionType, setActionType] = useState<ActionType>("add-to-existing");
  const [isClosing, setIsClosing] = useState(false);
  const [currentStep, setCurrentStep] =
    useState<CurrentStep>("select-action-step");
  const [successResult, setSuccessResult] = useState<SuccessResult | null>(
    null,
  );

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (isClosing) {
      timeoutId = setTimeout(() => {
        setIsClosing(false);
        setCurrentStep("select-action-step");
        setActionType("add-to-existing");
        setSuccessResult(null);
      }, 300);
    }

    return () => clearTimeout(timeoutId);
  }, [isClosing]);

  const handleClose = () => {
    onModalClose();
    setIsClosing(true);
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4"
              enterTo="opacity-100 translate-y-0"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-4"
            >
              <Dialog.Panel className="relative transform overflow-visible rounded-lg bg-white text-left shadow-xl transition-all mx-4 mb-24 w-full">
                <div className="absolute top-0 right-0 pt-4 pr-4 z-10">
                  <XMarkIcon
                    className="h-5 w-auto text-gray-400"
                    aria-hidden="true"
                    onClick={handleClose}
                  />
                </div>
                {currentStep === "select-action-step" && (
                  <SelectActionScreen
                    workoutTitle={workout?.title || "Untitled Workout"}
                    selectedOption={actionType}
                    setSelectedOption={setActionType}
                    setCurrentStep={setCurrentStep}
                  />
                )}
                {currentStep === "configure-step" &&
                  actionType === "add-to-existing" && (
                    <AddToExistingProgramScreen
                      workoutId={workout?.id || ""}
                      workoutTitle={workout?.title || "Untitled Workout"}
                      programs={programs}
                      setCurrentStep={setCurrentStep}
                      setSuccessResult={setSuccessResult}
                    />
                  )}
                {currentStep === "configure-step" &&
                  actionType === "create-new-program" && (
                    <CreateNewProgramScreen
                      workoutId={workout?.id || ""}
                      workoutTitle={workout?.title || "Untitled Workout"}
                      setCurrentStep={setCurrentStep}
                      setSuccessResult={setSuccessResult}
                    />
                  )}
                {currentStep === "success-step" && successResult && (
                  <SuccessScreen
                    successResult={successResult}
                    onClose={handleClose}
                  />
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

// ─── Step 1: Select Action ───────────────────────────────────────────────────

interface SelectActionScreenProps {
  workoutTitle: string;
  selectedOption: ActionType;
  setSelectedOption: React.Dispatch<React.SetStateAction<ActionType>>;
  setCurrentStep: React.Dispatch<React.SetStateAction<CurrentStep>>;
}

const SelectActionScreen: React.FC<SelectActionScreenProps> = ({
  workoutTitle,
  selectedOption,
  setSelectedOption,
  setCurrentStep,
}) => {
  return (
    <>
      <div className="bg-white px-4 pt-5 pb-4 rounded-lg">
        <ModalHeader
          title="Add Workout to Program"
          paragraph={`Save "${workoutTitle}" as a template in a program.`}
        />
        <div className="flex flex-col gap-y-4 items-center">
          <ActionRadioOption
            value="add-to-existing"
            title="Add to Existing Program"
            paragraph="Choose one of your existing programs and add this workout as a new template."
            icon={
              <FolderOpenIcon
                className="h-auto w-[24px] text-indigo-600"
                aria-hidden="true"
              />
            }
            selectedOption={selectedOption}
            setSelectedOption={setSelectedOption}
          />
          <ActionRadioOption
            value="create-new-program"
            title="Create a New Program"
            paragraph="Start a brand new program with this workout as its first template."
            icon={
              <FolderPlusIcon
                className="h-auto w-[24px] text-indigo-600"
                aria-hidden="true"
              />
            }
            selectedOption={selectedOption}
            setSelectedOption={setSelectedOption}
          />
        </div>
      </div>
      <div className="bg-gray-50 rounded-lg px-4 py-3">
        <div className="mb-3 w-full flex justify-center gap-x-2">
          <div className="w-[7px] h-[7px] bg-indigo-600 rounded-full"></div>
          <div className="w-[7px] h-[7px] bg-gray-400/40 rounded-full"></div>
        </div>
        <FullWidthButton
          className=""
          text="Next"
          onClick={() => {
            setCurrentStep("configure-step");
          }}
        />
      </div>
    </>
  );
};

// ─── Radio Option Card ───────────────────────────────────────────────────────

interface ActionRadioOptionProps {
  value: string;
  title: string;
  paragraph: string;
  icon: React.ReactNode;
  selectedOption: ActionType;
  setSelectedOption: React.Dispatch<React.SetStateAction<ActionType>>;
}

const ActionRadioOption: React.FC<ActionRadioOptionProps> = ({
  value,
  title,
  paragraph,
  icon,
  selectedOption,
  setSelectedOption,
}) => {
  return (
    <div
      className={classNames(
        "relative flex flex-row gap-x-4 items-center p-2 border border-gray-300 rounded-lg",
        {
          "border-indigo-600": selectedOption === value,
        },
      )}
      key={value}
      onClick={() => setSelectedOption(value as ActionType)}
    >
      <input
        id={value}
        value={value}
        name="add-workout-action"
        type="radio"
        className="absolute top-0 right-0 m-2 h-4 w-4 checked:bg-indigo-600 bg-indigo-600 text-indigo-600 focus:ring-indigo-600 hidden"
        tabIndex={-1}
        checked={selectedOption === value}
        onChange={() => {
          setSelectedOption(value as ActionType);
        }}
      />
      {selectedOption === value && (
        <CheckCircleIcon className="absolute top-0 right-0 m-2 h-auto w-5 rounded-full text-indigo-600" />
      )}
      <div>
        <div className="inline-flex p-2 items-center justify-center rounded-xl bg-indigo-100">
          {icon}
        </div>
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium leading-6 text-gray-900">
          {title}
        </label>
        <p className="text-xs text-gray-500 whitespace-pre-wrap">{paragraph}</p>
      </div>
    </div>
  );
};

// ─── Step 2a: Add to Existing Program ────────────────────────────────────────

interface AddToExistingProgramScreenProps {
  workoutId: string;
  workoutTitle: string;
  programs: Program[];
  setCurrentStep: React.Dispatch<React.SetStateAction<CurrentStep>>;
  setSuccessResult: React.Dispatch<React.SetStateAction<SuccessResult | null>>;
}

const AddToExistingProgramScreen: React.FC<AddToExistingProgramScreenProps> = ({
  workoutId,
  workoutTitle,
  programs,
  setCurrentStep,
  setSuccessResult,
}) => {
  const [selectedProgram, setSelectedProgram] = useState<Program | null>(null);

  const handleProgramSelected = (programId: string) => {
    setSelectedProgram(
      programs.find((program) => program._id === programId) || null,
    );
  };

  const handleAddToProgram = async () => {
    if (!selectedProgram) return;

    const response = await axiosClient.post(
      `/api/workout-templates/create-from-workout`,
      {
        workoutId: workoutId,
        programId: selectedProgram._id,
      },
    );

    if (response.status !== 201) {
      throw new Error(
        response.data.error || "Failed to add workout to program.",
      );
    }

    const createdTemplate = response.data;

    setSuccessResult({
      programId: selectedProgram._id,
      workoutTemplateId: createdTemplate._id,
      programTitle: selectedProgram.title,
    });
    setCurrentStep("success-step");
  };

  return (
    <>
      <div className="bg-white rounded-lg px-4 pt-5 pb-3">
        <ModalHeader
          title="Select a Program"
          paragraph={`Choose which program to add "${workoutTitle}" to as a template.`}
        />
        <SuggestionsInput
          id="select-program"
          label="Program"
          placeholder="Search for a program"
          suggestions={programs}
          selectedValue={
            selectedProgram && {
              _id: selectedProgram._id,
              name: selectedProgram.title,
            }
          }
          onSelect={handleProgramSelected}
          suggestionLabelEntityProperty="title"
          entityName="Program"
          createEntityLink="/programs"
        />
        {selectedProgram && (
          <div className="mt-4">
            <p className="mb-1 text-xs font-medium text-gray-500 uppercase tracking-wide">
              Adding to
            </p>
            <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
              <p className="text-sm font-medium text-indigo-900">
                {selectedProgram.title}
              </p>
              <p className="text-xs text-indigo-600 mt-1">
                {selectedProgram.workoutTemplates.length}{" "}
                {selectedProgram.workoutTemplates.length === 1
                  ? "workout template"
                  : "workout templates"}{" "}
                currently
              </p>
            </div>
          </div>
        )}
      </div>
      <div className="bg-gray-50 rounded-lg px-4 py-3">
        <div className="mb-3 w-full flex justify-center gap-x-2">
          <div className="w-[7px] h-[7px] bg-gray-400/40 rounded-full"></div>
          <div className="w-[7px] h-[7px] bg-indigo-600 rounded-full"></div>
        </div>
        <FullWidthAsyncButton
          className="mb-3"
          text="Add to Program"
          loadingText="Adding to Program"
          onClick={handleAddToProgram}
          disabled={!selectedProgram}
        />
        <button
          type="button"
          className="inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 font-medium text-gray-700 shadow-sm text-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          onClick={() => {
            setCurrentStep("select-action-step");
          }}
        >
          Back
        </button>
      </div>
    </>
  );
};

// ─── Step 2b: Create New Program ─────────────────────────────────────────────

interface CreateNewProgramScreenProps {
  workoutId: string;
  workoutTitle: string;
  setCurrentStep: React.Dispatch<React.SetStateAction<CurrentStep>>;
  setSuccessResult: React.Dispatch<React.SetStateAction<SuccessResult | null>>;
}

const CreateNewProgramScreen: React.FC<CreateNewProgramScreenProps> = ({
  workoutId,
  workoutTitle,
  setCurrentStep,
  setSuccessResult,
}) => {
  const [programTitle, setProgramTitle] = useState("");
  const [programDescription, setProgramDescription] = useState("");

  const handleCreateProgram = async () => {
    if (!programTitle.trim()) return;

    const response = await axiosClient.post(
      `/api/workout-templates/create-from-workout-with-new-program`,
      {
        workoutId: workoutId,
        programTitle: programTitle.trim(),
        programDescription: programDescription.trim() || undefined,
      },
    );

    if (response.status !== 201) {
      throw new Error(
        response.data.error || "Failed to create program and add workout.",
      );
    }

    const { program, workoutTemplate } = response.data;

    setSuccessResult({
      programId: program._id,
      workoutTemplateId: workoutTemplate._id,
      programTitle: program.title,
    });
    setCurrentStep("success-step");
  };

  return (
    <>
      <div className="bg-white rounded-lg px-4 pt-5 pb-3">
        <ModalHeader
          title="Create a New Program"
          paragraph={`Set up a new program with "${workoutTitle}" as its first template.`}
        />
        <div className="flex flex-col gap-y-4">
          <div>
            <label
              htmlFor="program-title"
              className="block mb-1 text-sm font-medium text-gray-700"
            >
              Program Name
            </label>
            <input
              id="program-title"
              name="program-title"
              type="text"
              autoComplete="off"
              tabIndex={-1}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
              placeholder="e.g. Push Pull Legs, Upper Lower..."
              value={programTitle}
              onChange={(e) => setProgramTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  (e.target as HTMLInputElement).blur();
                }
              }}
            />
          </div>
          <div>
            <label
              htmlFor="program-description"
              className="block mb-1 text-sm font-medium text-gray-700"
            >
              Description{" "}
              <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              id="program-description"
              name="program-description"
              autoComplete="off"
              tabIndex={-1}
              rows={3}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
              placeholder="Briefly describe this program..."
              value={programDescription}
              onChange={(e) => setProgramDescription(e.target.value)}
            />
          </div>
          <div>
            <p className="mb-1 text-xs font-medium text-gray-500 uppercase tracking-wide">
              Will be added as a template
            </p>
            <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
              <p className="text-sm font-medium text-indigo-900">
                {workoutTitle}
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-gray-50 rounded-lg px-4 py-3">
        <div className="mb-3 w-full flex justify-center gap-x-2">
          <div className="w-[7px] h-[7px] bg-gray-400/40 rounded-full"></div>
          <div className="w-[7px] h-[7px] bg-indigo-600 rounded-full"></div>
        </div>
        <FullWidthAsyncButton
          className="mb-3"
          text="Create Program & Add Workout"
          loadingText="Creating Program"
          onClick={handleCreateProgram}
          disabled={!programTitle.trim()}
        />
        <button
          type="button"
          className="inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 font-medium text-gray-700 shadow-sm text-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          onClick={() => {
            setCurrentStep("select-action-step");
          }}
        >
          Back
        </button>
      </div>
    </>
  );
};

// ─── Success Screen ──────────────────────────────────────────────────────────

interface SuccessScreenProps {
  successResult: SuccessResult;
  onClose: () => void;
}

const SuccessScreen: React.FC<SuccessScreenProps> = ({
  successResult,
  onClose,
}) => {
  const navigate = useNavigate();

  const handleCustomizeTemplate = () => {
    onClose();
    navigate(
      `/programs/${successResult.programId}/workout-templates/${successResult.workoutTemplateId}`,
    );
  };

  return (
    <>
      <div className="bg-white rounded-lg px-4 pt-5 pb-4">
        <div className="mb-4">
          <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-green-100">
            <CheckCircleIcon
              className="h-6 w-6 text-green-600"
              aria-hidden="true"
            />
          </div>
          <Dialog.Title
            as="h3"
            className="mt-3 text-xl font-medium leading-6 text-gray-900 w-full text-center"
          >
            Workout Added!
          </Dialog.Title>
          <p className="mt-2 text-center text-sm text-gray-500">
            Your workout has been added as a template to{" "}
            <span className="font-medium text-gray-700">
              {successResult.programTitle}
            </span>
            .
          </p>
        </div>
        <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
          <p className="text-xs text-indigo-700">
            <span className="font-medium text-indigo-900">Tip:</span> You can
            customize the template to add rep ranges, rest times, tempo, and
            other details that help structure your future workouts.
          </p>
        </div>
      </div>
      <div className="bg-gray-50 rounded-lg px-4 py-3">
        <FullWidthButton
          className="mb-3"
          text="Customize Template"
          onClick={handleCustomizeTemplate}
        />
        <button
          type="button"
          className="inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 font-medium text-gray-700 shadow-sm text-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          onClick={onClose}
        >
          Done
        </button>
      </div>
    </>
  );
};

// ─── Shared Modal Header ─────────────────────────────────────────────────────

interface ModalHeaderProps {
  title: string;
  paragraph?: string;
}

const ModalHeader: React.FC<ModalHeaderProps> = ({ title, paragraph }) => {
  return (
    <div className="mb-4">
      <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100">
        <PlusCircleIcon
          className="h-6 w-6 text-indigo-600"
          aria-hidden="true"
        />
      </div>
      <Dialog.Title
        as="h3"
        className="mt-3 text-xl font-medium leading-6 text-gray-900 w-full text-center"
      >
        {title}
      </Dialog.Title>
      {paragraph && (
        <p className="mt-2 text-center text-sm text-gray-500 whitespace-pre-wrap">
          {paragraph}
        </p>
      )}
    </div>
  );
};

export default AddWorkoutToProgramModal;
